import React, { useEffect, useMemo } from 'react';
import { ActionSheetManagerContextProvider } from '@/contexts/action-sheet-manager-context';
import { AddItemSearchScreen } from '@/screens/order-inbounds/add-item-search-screen';
import { AddPADScreen } from '@/screens/payments/add-pad-screen';
import { AddPaymentMethodScreen } from '@/screens/payments/add-payment-method-screen';
import { CorrectionItemSearchScreen } from '@/screens/order-inbounds/correction-item-search-screen';
import { createStackNavigator } from '@react-navigation/stack';
import { createStyles } from '@/contexts/theme-context';
import { DRAWER_HEADER_HEIGHT } from '@/styles';
import { DrawerHeaderLarge } from '@/components/drawer-navigation/drawer-header-large';
import { DSRToolingScreen } from '@/screens/dsr-tooling-screen';
import { DummyAdsContextProvider } from '@/contexts/ads-context';
import { DummyItemRecommendationsContextProvider } from '@/contexts/item-recommendations-context';
import { DummyOfflineDownloadManagementContextProvider } from '@/contexts/offline-download-management-context';
import { DummyOrderGuideSettingsContextProvider } from '@/contexts/order-guide-settings-context';
import { EditOrderItemNoteModalScreen } from '@/screens/partners/edit-order-item-note-modal-screen';
import { ItemDetailContextProvider } from '@/contexts/item-detail-context';
import { ItemLeadTimeContextProvider } from '@/contexts/item-lead-time-context';
import { MultiOrderGuideItemsContextProvider } from '@/contexts/multi-order-guide-items-context-provider';
import { NewOrderActionsContextProvider } from '@/contexts/new-order-actions-context';
import { NewOrderContextProvider } from '@/contexts/new-order-context';
import { NewOrderQuantityStateContextProvider } from '@/contexts/new-order-quantity-state-context';
import { NewOrderStack } from './new-order-stack';
import { ORDER_INBOUNDS_ROUTES } from './routes';
import { OrderDomain } from '@/types';
import { OrderGuideStoreProvider } from '@/stores/order-guide-store';
import { OrderInboundDrawerContextProvider } from '@/contexts/order-inbound-drawer-context-provider';
import { OrderInboundsDetailScreen } from '@/screens/order-inbounds/order-inbounds-detail-screen';
import { OrderInboundsListScreen } from '@/screens/order-inbounds/order-inbounds-list-screen';
import { OrderInboundsOrderListDrawerProvider } from '@/screens/order-inbounds/order-inbounds-order-list-drawer/order-inbounds-order-list-drawer-context';
import { OrderInboundsStackParams } from './types';
import {
  PaymentsContextProvider,
  PaymentsDataScope,
} from '@/contexts/payments-context';
import { Platform, View } from 'react-native';
import { requiresPaymentAtOrderPlacement } from '@/utils/payments';
import { SwapItemSearchScreen } from '@/screens/order-inbounds/swap-item-search-screen';
import { TabCardApplicationScreen } from '@/screens/payments/tab-card-application-screen';
import { ThreadOrderEventsProvider } from '@/screens/order-inbounds/thread-order-events-context';
import { ToastContextProvider } from '@/contexts/toast-context';
import {
  useAuthenticatedUserContext,
  useUserContext,
} from '@/contexts/user-context';
import { useDefaultStackScreenOptions } from '@/hooks/use-default-stack-screen-options';
import { useInboundCartInputs } from '@/hooks/use-inbound-cart-inputs';
import { useInboundFulfillment } from '@/hooks/use-inbound-fulfillment';
import { useLayoutContext } from '@/contexts/layout-context';
import { useModalStackScreenOptions } from '@/hooks/use-modal-stack-screen-options';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { UserContextWithOverridesProvider } from '@/contexts/user-overrides-context';
import { VariantPackInfoContextProvider } from '@/contexts/variant-pack-info-context';
import { VolumePromotionsContextProvider } from '@/contexts/volume-promotions-context';

const Stack = createStackNavigator<OrderInboundsStackParams>();

// Skip fetching data that isn't needed for the overridden restaurant.
// We need a separate provider so the useUserContext hook uses the override
const InboundPaymentsContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { selectedRestaurantRelationship } = useUserContext();

  return (
    <PaymentsContextProvider
      dataScope={
        requiresPaymentAtOrderPlacement(selectedRestaurantRelationship)
          ? PaymentsDataScope.PaymentMethods
          : PaymentsDataScope.None
      }
    >
      {children}
    </PaymentsContextProvider>
  );
};

export const OrderInboundsStack: React.FC = () => {
  const options = useInboundStackScreenOptions();
  const modalStackScreenOptions = useModalStackScreenOptions();
  const { isLargeLayout, isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();
  const setIsInOrderInboundsStack = useOrderInboundsStore(
    (state) => state.actions.setIsInOrderInboundsStack,
  );
  const setActiveEmployeeUUID = useOrderInboundsStore(
    (state) => state.actions.setActiveEmployeeUUID,
  );
  const { user } = useAuthenticatedUserContext();

  // Restore this employee's last-applied list filters from device storage
  // before the list screen mounts, so its first query already uses them.
  useEffect(() => {
    setActiveEmployeeUUID(user.uuid);
  }, [setActiveEmployeeUUID, user.uuid]);

  useEffect(() => {
    setIsInOrderInboundsStack(true);
    return () => {
      setIsInOrderInboundsStack(false);
    };
  }, [setIsInOrderInboundsStack]);

  return (
    <ToastContextProvider
      containerStyle={
        Platform.OS !== 'web' || isSmallLayout ? undefined : styles.headerOffset
      }
    >
      <UserContextWithOverridesProvider>
        <DummyOrderGuideSettingsContextProvider>
          <ActionSheetManagerContextProvider>
            <DummyOfflineDownloadManagementContextProvider>
              <InboundPaymentsContextProvider>
                <NewOrderQuantityStateContextProvider>
                  <OrderGuideStoreProvider>
                    <NewOrderContextProvider
                      orderDomain={OrderDomain.OrderAgent}
                      skipRefetchOnFocus
                      trackLoading
                    >
                      <NewOrderActionsContextProvider
                        orderDomain={OrderDomain.OrderAgent}
                        useCartInputsHook={useInboundCartInputs}
                        useFulfillmentHook={useInboundFulfillment}
                      >
                        <VariantPackInfoContextProvider skipLastPurchaseInfo>
                          <DummyAdsContextProvider>
                            <ItemLeadTimeContextProvider>
                              <VolumePromotionsContextProvider>
                                <MultiOrderGuideItemsContextProvider>
                                  <DummyItemRecommendationsContextProvider>
                                    <ItemDetailContextProvider>
                                      <ThreadOrderEventsProvider>
                                        <OrderInboundDrawerContextProvider>
                                          <OrderInboundsOrderListDrawerProvider>
                                            <View style={styles.largeWrapper}>
                                              <Stack.Navigator
                                                initialRouteName={
                                                  ORDER_INBOUNDS_ROUTES.List
                                                }
                                                screenOptions={options}
                                              >
                                                <Stack.Screen
                                                  component={
                                                    OrderInboundsListScreen
                                                  }
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.List
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={
                                                    OrderInboundsDetailScreen
                                                  }
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.Detail
                                                  }
                                                  options={{
                                                    cardStyle: styles.card,
                                                  }}
                                                />
                                                <Stack.Screen
                                                  component={
                                                    SwapItemSearchScreen
                                                  }
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.SwapSearch
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={
                                                    CorrectionItemSearchScreen
                                                  }
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.CorrectionSearch
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={
                                                    AddItemSearchScreen
                                                  }
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.AddSearch
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={NewOrderStack}
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.Submit
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={AddPADScreen}
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.AddPAD
                                                  }
                                                  options={
                                                    modalStackScreenOptions
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={
                                                    AddPaymentMethodScreen
                                                  }
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.AddPaymentMethod
                                                  }
                                                  options={
                                                    modalStackScreenOptions
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={
                                                    TabCardApplicationScreen
                                                  }
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.TabCardApplication
                                                  }
                                                  options={
                                                    modalStackScreenOptions
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={DSRToolingScreen}
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.DSRTooling
                                                  }
                                                  options={
                                                    modalStackScreenOptions
                                                  }
                                                />
                                                <Stack.Screen
                                                  component={
                                                    EditOrderItemNoteModalScreen
                                                  }
                                                  name={
                                                    ORDER_INBOUNDS_ROUTES.EditOrderItemNote
                                                  }
                                                  options={
                                                    modalStackScreenOptions
                                                  }
                                                />
                                              </Stack.Navigator>
                                              {isLargeLayout ? (
                                                <DrawerHeaderLarge
                                                  hideShadow
                                                  inInboundsStack
                                                />
                                              ) : null}
                                            </View>
                                          </OrderInboundsOrderListDrawerProvider>
                                        </OrderInboundDrawerContextProvider>
                                      </ThreadOrderEventsProvider>
                                    </ItemDetailContextProvider>
                                  </DummyItemRecommendationsContextProvider>
                                </MultiOrderGuideItemsContextProvider>
                              </VolumePromotionsContextProvider>
                            </ItemLeadTimeContextProvider>
                          </DummyAdsContextProvider>
                        </VariantPackInfoContextProvider>
                      </NewOrderActionsContextProvider>
                    </NewOrderContextProvider>
                  </OrderGuideStoreProvider>
                </NewOrderQuantityStateContextProvider>
              </InboundPaymentsContextProvider>
            </DummyOfflineDownloadManagementContextProvider>
          </ActionSheetManagerContextProvider>
        </DummyOrderGuideSettingsContextProvider>
      </UserContextWithOverridesProvider>
    </ToastContextProvider>
  );
};

const useInboundStackScreenOptions = () => {
  const { isLargeLayout, isSmallLayout } = useLayoutContext();
  const options = useDefaultStackScreenOptions();

  /**
   * Render DrawerHeaderLarge because this stack does not have access to the
   * tab routes in the app tabs navigator, which the DrawerHeaderTablet uses.
   * But don't render on large layouts, because it's already rendered above in
   * the AppTabsContainerStack.
   */
  return useMemo(
    () => ({
      ...options,
      header: () => <DrawerHeaderLarge hideShadow inInboundsStack />,
      headerShown: !isLargeLayout && !isSmallLayout,
    }),
    [options, isLargeLayout, isSmallLayout],
  );
};

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    flex: 1,
  },
  headerOffset: {
    top: DRAWER_HEADER_HEIGHT,
  },
  largeWrapper: {
    flex: 1,
    flexDirection: 'column-reverse',
  },
}));
